/**
 * AB#22510 — the "Waiting for consolidation" Move option for one order item.
 *
 * The backend decides whether the item may wait (`can_wait_for_consolidation`)
 * and, when it may not, why (`waiting_block_reason`). The option is always
 * offered so the admin can see the step exists; when it is blocked it is shown
 * disabled with the reason, and the picker never highlights it as "next".
 * The hint strings match the backend's validation messages word for word.
 */
import type { MoveOption } from '@/components/Dashboard/Order/components/MoveOrderPicker.vue';

export const WAITING_FOR_CONSOLIDATION = 'waiting_for_consolidation';

export type WaitingBlockReason = 'status' | 'qc_pending' | 'qc_rejected';

export const WAITING_BLOCK_HINTS: Record<WaitingBlockReason, string> = {
  status: 'Only an order in production or at quality control can wait for consolidation.',
  qc_pending: 'Push the order to QC and wait for the Final inspection to pass first.',
  qc_rejected: 'The Final inspection was rejected; the order cannot wait for consolidation.',
};

export const waitingForConsolidationOption = (item: {
  can_wait_for_consolidation?: boolean | null;
  waiting_block_reason?: WaitingBlockReason | null;
}): MoveOption => {
  const disabled = !item.can_wait_for_consolidation;
  const reason = item.waiting_block_reason;
  return {
    value: WAITING_FOR_CONSOLIDATION,
    text: 'Waiting for consolidation',
    disabled,
    hint: disabled && reason ? WAITING_BLOCK_HINTS[reason] : undefined,
  };
};

/**
 * AB#22503 QA — from Waiting the backend accepts only Shipped, On hold and Cancel
 * (ConsolidationStatusRules::ALLOWED_FROM_WAITING). On hold has its own menu item,
 * so the picker keeps Shipped and Cancelled open and disables the rest. The
 * backend's sentence is the picker's notice (said once) and each locked option's
 * tooltip. The current Waiting option keeps no hint.
 */
export const ALLOWED_FROM_WAITING = ['shipped', 'order_cancel'];

export const ONLY_SHIPPED_HINT = 'From Waiting for consolidation an order can only be shipped.';

export const lockWhileWaiting = (
  options: MoveOption[],
  currentStatus: string | null | undefined
): MoveOption[] => {
  if (currentStatus !== WAITING_FOR_CONSOLIDATION) return options;
  return options.map((o) => {
    if (o.value === WAITING_FOR_CONSOLIDATION) return { ...o, disabled: true, hint: undefined };
    if (ALLOWED_FROM_WAITING.includes(o.value)) return o;
    return { ...o, disabled: true, hint: ONLY_SHIPPED_HINT, quiet: true };
  });
};

/**
 * AB#22503 QA — a waiting item keeps the est. ship date and shipping-details
 * buttons it had in production: its date moves with the consolidation group.
 */
export const isProductionOrWaiting = (status: string | null | undefined): boolean =>
  status === 'in_production' || status === WAITING_FOR_CONSOLIDATION;
